import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'react-toastify'
import {
  AdminApi,
  AdminWorkshopRegistrationItem,
  WorkshopEmailLogItem,
  WorkshopEmailLogStats,
  WorkshopConfig,
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const WORKSHOP_ID = 'ai4sdlc-2026-09-27'
const POLL_INTERVAL = 3000 // 3s poll

// ─── Status badge ───────────────────────────────────────────
const statusBadge = (status: WorkshopEmailLogItem['status']) => {
  if (status === 'success')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">✅ Thành công</span>
  if (status === 'failed')
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">❌ Thất bại</span>
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">⏳ Đang gửi</span>
}

// ─── Progress Bar component ─────────────────────────────────
const ProgressBar = ({ stats, total }: { stats: WorkshopEmailLogStats; total: number }) => {
  const successPct = total > 0 ? Math.round((Number(stats.success_count) / total) * 100) : 0
  const failedPct = total > 0 ? Math.round((Number(stats.failed_count) / total) * 100) : 0
  const donePct = successPct + failedPct
  const isPending = Number(stats.pending_count) > 0

  return (
    <div className="bg-m2-surface-container border border-blue-200 rounded-xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isPending ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse inline-block" />
              Đang gửi email...
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
              Hoàn thành
            </span>
          )}
        </div>
        <span className="text-sm font-bold text-m2-on-surface">
          {Number(stats.success_count) + Number(stats.failed_count)} / {total}
        </span>
      </div>

      {/* Progress bar */}
      <div className="relative h-4 bg-m2-surface-low rounded-full overflow-hidden">
        {/* Success */}
        <div
          className="absolute left-0 top-0 h-full bg-green-500 transition-all duration-500"
          style={{ width: `${successPct}%` }}
        />
        {/* Failed */}
        <div
          className="absolute top-0 h-full bg-red-400 transition-all duration-500"
          style={{ left: `${successPct}%`, width: `${failedPct}%` }}
        />
        {/* Pending shimmer */}
        {isPending && (
          <div
            className="absolute top-0 h-full opacity-30"
            style={{
              left: `${donePct}%`,
              width: `${100 - donePct}%`,
              background: 'repeating-linear-gradient(45deg,#3b82f6,#3b82f6 10px,#93c5fd 10px,#93c5fd 20px)',
              backgroundSize: '28px 28px',
              animation: 'moveStripes 1s linear infinite',
            }}
          />
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3 text-center text-sm">
        <div className="bg-m2-surface-low rounded-lg py-2">
          <div className="font-bold text-lg text-m2-on-surface">{total}</div>
          <div className="text-xs text-m2-on-surface-variant">Tổng</div>
        </div>
        <div className="bg-green-50 rounded-lg py-2">
          <div className="font-bold text-lg text-green-700">{stats.success_count}</div>
          <div className="text-xs text-green-600">Thành công</div>
        </div>
        <div className="bg-red-50 rounded-lg py-2">
          <div className="font-bold text-lg text-red-700">{stats.failed_count}</div>
          <div className="text-xs text-red-600">Thất bại</div>
        </div>
        <div className="bg-yellow-50 rounded-lg py-2">
          <div className="font-bold text-lg text-yellow-700">{stats.pending_count}</div>
          <div className="text-xs text-yellow-600">Còn lại</div>
        </div>
      </div>

      {isPending && (
        <p className="text-xs text-m2-on-surface-variant text-center">
          ⏱️ Ước tính thêm ~{Math.ceil(Number(stats.pending_count) * 2 / 60)} phút ({Number(stats.pending_count)} email × 2s)
        </p>
      )}
    </div>
  )
}

// ─── Send Meet Modal ────────────────────────────────────────
interface SendMeetModalProps {
  initialMeetLink?: string
  onClose: () => void
  onSent: (total: number, sentMeetLink: string) => void
}

const SendMeetModal = ({ initialMeetLink, onClose, onSent }: SendMeetModalProps) => {
  const [meetLink, setMeetLink] = useState(initialMeetLink || '')
  const [force, setForce] = useState(false)
  const [sending, setSending] = useState(false)

  const handleSend = async () => {
    const trimmed = meetLink.trim()
    if (!trimmed.startsWith('http')) {
      toast.error('Link Google Meet không hợp lệ. Vui lòng nhập đúng URL.')
      return
    }
    setSending(true)
    try {
      const res = await AdminApi.sendWorkshopMeetLink({
        meet_link: trimmed,
        workshop_id: WORKSHOP_ID,
        force,
      })
      if (res.success) {
        toast.success(res.message)
        onSent(res.total ?? 0, trimmed)
        onClose()
      } else {
        toast.error(res.message || 'Có lỗi xảy ra.')
      }
    } catch {
      toast.error('Không thể gửi email. Vui lòng thử lại.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-m2-surface-container rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-5 flex items-center gap-3">
          <div className="text-3xl">📹</div>
          <div>
            <h2 className="text-lg font-bold text-white">Gửi Link Google Meet</h2>
            <p className="text-sm text-blue-200 mt-0.5">AI4SDLC Workshop · 27/09/2026 · 20:00–22:00</p>
          </div>
          <button onClick={onClose} className="ml-auto text-blue-200 hover:text-white text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-m2-on-surface mb-2">
              Link Google Meet <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={meetLink}
              onChange={(e) => setMeetLink(e.target.value)}
              placeholder="https://meet.google.com/xxx-xxxx-xxx"
              className="w-full px-4 py-2.5 rounded-lg border border-m2-outline-variant bg-m2-input text-m2-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-m2-on-surface-variant/40"
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="mt-0.5 w-4 h-4 accent-blue-600" />
            <div>
              <p className="text-sm font-medium text-m2-on-surface">Gửi lại cho tất cả (kể cả đã thành công)</p>
              <p className="text-xs text-m2-on-surface-variant mt-0.5">Mặc định chỉ gửi cho người <strong>chưa nhận được</strong>.</p>
            </div>
          </label>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm space-y-1.5">
            <p className="font-semibold text-blue-800">📧 Nội dung email gồm:</p>
            <ul className="list-disc list-inside text-xs text-blue-700 space-y-1">
              <li>Link Google Meet + thông tin buổi học</li>
              <li>Lưu ý vào trước <strong>10 phút</strong></li>
              <li>Thông báo có thể chậm approve nếu vào muộn</li>
              <li>Record sẽ upload YouTube</li>
            </ul>
            <p className="text-xs text-blue-600 mt-2">⏱️ Tốc độ: <strong>2 giây / email</strong> · ~300 mail ≈ 10 phút</p>
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2 rounded-lg text-sm font-medium border border-m2-outline-variant text-m2-on-surface hover:bg-m2-surface-low transition-colors">
            Hủy
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !meetLink.trim()}
            className="px-5 py-2 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {sending ? <><span className="animate-spin inline-block">⏳</span> Đang khởi động...</> : <>📨 Gửi Email Meet Link</>}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ──────────────────────────────────────────────
const AdminWorkshopRegistrationsPage = () => {
  // Registration list
  const [items, setItems] = useState<AdminWorkshopRegistrationItem[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)

  // Modal
  const [showSendModal, setShowSendModal] = useState(false)

  // Progress / Log
  const [logs, setLogs] = useState<WorkshopEmailLogItem[]>([])
  const [logStats, setLogStats] = useState<WorkshopEmailLogStats | null>(null)
  const [logsLoading, setLogsLoading] = useState(false)
  const [showLogs, setShowLogs] = useState(false)
  const [selectedFailedIds, setSelectedFailedIds] = useState<number[]>([])
  const [retrying, setRetrying] = useState(false)

  // Sending progress tracking
  const [isSending, setIsSending] = useState(false)
  const [sendTotal, setSendTotal] = useState(0)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Config
  const [config, setConfig] = useState<WorkshopConfig | null>(null)
  const [configLoading, setConfigLoading] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [meetLinkConfig, setMeetLinkConfig] = useState('')
  const [configSaving, setConfigSaving] = useState(false)

  const loadData = async () => {
    setLoading(true)
    setConfigLoading(true)
    try {
      const [regRes, configRes] = await Promise.all([
        AdminApi.getWorkshopRegistrations({ page, limit: 20, search: search || undefined, workshop_id: WORKSHOP_ID }),
        AdminApi.getWorkshopConfig(WORKSHOP_ID)
      ])
      
      setItems(regRes.data.items)
      setTotalPages(regRes.data.pagination.totalPages)
      setTotal(regRes.data.pagination.total)

      if (configRes.success && configRes.data) {
        setConfig(configRes.data)
        setYoutubeUrl(configRes.data.youtube_url || '')
        setMeetLinkConfig(configRes.data.meet_link || '')
      }
    } catch {
      toast.error('Không tải được danh sách đăng ký hoặc cấu hình workshop')
    } finally {
      setLoading(false)
      setConfigLoading(false)
    }
  }

  const loadLogs = useCallback(async () => {
    setLogsLoading(true)
    try {
      const res = await AdminApi.getWorkshopEmailLogs(WORKSHOP_ID)
      if (res.success) {
        setLogs(res.data.logs)
        setLogStats(res.data.stats)

        // Stop polling khi hết pending
        const pending = Number(res.data.stats.pending_count)
        if (pending === 0 && isSending) {
          setIsSending(false)
          const successCount = Number(res.data.stats.success_count)
          const failedCount = Number(res.data.stats.failed_count)
          toast.success(`✅ Hoàn thành! Thành công: ${successCount}, Thất bại: ${failedCount}`)
        }
      }
    } catch {
      // silent fail for polling
    } finally {
      setLogsLoading(false)
    }
  }, [isSending])

  // Start/stop polling
  useEffect(() => {
    if (isSending) {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(() => void loadLogs(), POLL_INTERVAL)
      void loadLogs() // immediate first call
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [isSending, loadLogs])

  useEffect(() => {
    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search])

  useEffect(() => {
    if (showLogs && !isSending) void loadLogs()
  }, [showLogs, isSending, loadLogs])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput)
  }

  const handleExportCsv = () => {
    if (items.length === 0) { toast.info('Không có dữ liệu để xuất.'); return }
    const headers = ['STT', 'Họ và tên', 'Email', 'Số điện thoại', 'Vai trò', 'Workshop', 'Ngày đăng ký']
    const rows = items.map((item, i) => [
      (page - 1) * 20 + i + 1, `"${item.full_name}"`, item.email, item.phone,
      item.role || '', `"${item.workshop_name}"`, formatDbDateTimeVi(item.registered_at),
    ])
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `workshop-registrations-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleAfterSent = async (total: number, sentMeetLink: string) => {
    setSendTotal(total)
    setIsSending(true)
    setShowLogs(true)

    // Nếu gửi link mới, cập nhật vào config
    if (sentMeetLink && sentMeetLink !== config?.meet_link) {
      try {
        await AdminApi.updateWorkshopConfig(WORKSHOP_ID, {
          youtube_url: config?.youtube_url || null,
          meet_link: sentMeetLink
        })
        setConfig(prev => prev ? { ...prev, meet_link: sentMeetLink } : {
          id: 0,
          workshop_id: WORKSHOP_ID,
          youtube_url: null,
          meet_link: sentMeetLink,
          updated_at: ''
        })
        setMeetLinkConfig(sentMeetLink)
      } catch (err) {
        console.error('Không lưu được meet link vào config')
      }
    }
  }

  const toggleFailedSelect = (id: number) => {
    setSelectedFailedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  }

  const handleRetry = async () => {
    setRetrying(true)
    try {
      const res = await AdminApi.retryFailedWorkshopEmails({
        workshop_id: WORKSHOP_ID,
        log_ids: selectedFailedIds.length > 0 ? selectedFailedIds : undefined,
      })
      toast.success(res.message)
      setSelectedFailedIds([])
      // Start polling again for retry progress
      setSendTotal(res.retried ?? 0)
      setIsSending(true)
    } catch {
      toast.error('Không thể retry. Vui lòng thử lại.')
    } finally {
      setRetrying(false)
    }
  }

  const handleResendSingle = async (registrationId: number, currentMeetLink?: string) => {
    if (!config?.meet_link && !currentMeetLink) {
      toast.error('Chưa có link Meet trong cấu hình.')
      return
    }
    const idList = [registrationId]
    try {
      const res = await AdminApi.sendWorkshopMeetLink({
        meet_link: config?.meet_link || currentMeetLink || '',
        workshop_id: WORKSHOP_ID,
        force: true,
        registration_ids: idList,
      })
      if (res.success) {
        toast.success(res.message)
        setSendTotal(res.total ?? 1)
        setIsSending(true)
        setShowLogs(true)
      } else {
        toast.error(res.message)
      }
    } catch (err) {
      toast.error('Không thể gửi email.')
    }
  }

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault()
    setConfigSaving(true)
    try {
      const res = await AdminApi.updateWorkshopConfig(WORKSHOP_ID, {
        youtube_url: youtubeUrl || null,
        meet_link: meetLinkConfig || null
      })
      if (res.success) {
        toast.success(res.message)
        setConfig(prev => prev ? { ...prev, youtube_url: youtubeUrl || null, meet_link: meetLinkConfig || null } : null)
      } else {
        toast.error(res.message)
      }
    } catch (err) {
      toast.error('Không lưu được cấu hình.')
    } finally {
      setConfigSaving(false)
    }
  }

  const failedLogs = logs.filter((l) => l.status === 'failed')
  const progressTotal = sendTotal || (logStats ? Number(logStats.total) : 0)

  return (
    <div className="space-y-6">
      {/* CSS for stripe animation */}
      <style>{`
        @keyframes moveStripes {
          0% { background-position: 0 0; }
          100% { background-position: 28px 0; }
        }
      `}</style>

      {showSendModal && (
        <SendMeetModal 
          initialMeetLink={config?.meet_link || ''}
          onClose={() => setShowSendModal(false)} 
          onSent={handleAfterSent} 
        />
      )}

      {/* ── Live Progress Banner ─── */}
      {isSending && logStats && (
        <ProgressBar stats={logStats} total={progressTotal} />
      )}

      {/* ── Workshop Config ─── */}
      <div className="bg-m2-surface-container p-6 rounded-lg shadow-sm">
        <h2 className="text-xl font-bold text-m2-on-surface mb-4">Cài đặt Workshop (AI4SDLC)</h2>
        {configLoading ? (
           <div className="text-sm text-m2-on-surface-variant">Đang tải cấu hình...</div>
        ) : (
          <form onSubmit={handleSaveConfig} className="space-y-4 max-w-2xl">
            <div>
              <label className="block text-sm font-semibold text-m2-on-surface mb-1">
                Link YouTube (Bản record)
              </label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="VD: https://youtube.com/watch?v=..."
                className="w-full px-4 py-2 rounded-lg border border-m2-outline-variant bg-m2-input text-m2-on-surface text-sm focus:outline-none focus:ring-1 focus:ring-m2-primary"
              />
              <p className="text-xs text-m2-on-surface-variant mt-1">Sẽ hiện nút "Xem video trên YouTube" trên trang Workshop nếu có link này.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-m2-on-surface mb-1">
                Link Google Meet
              </label>
              <input
                type="url"
                value={meetLinkConfig}
                onChange={(e) => setMeetLinkConfig(e.target.value)}
                placeholder="VD: https://meet.google.com/xxx-xxxx-xxx"
                className="w-full px-4 py-2 rounded-lg border border-m2-outline-variant bg-m2-input text-m2-on-surface text-sm focus:outline-none focus:ring-1 focus:ring-m2-primary"
              />
            </div>
            <button
              type="submit"
              disabled={configSaving}
              className="px-5 py-2 rounded-lg text-sm font-bold bg-m2-primary text-m2-on-primary hover:bg-eh-forest-shadow disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {configSaving ? 'Đang lưu...' : 'Lưu Cài đặt'}
            </button>
          </form>
        )}
      </div>

      {/* ── Registration List ─── */}
      <div className="bg-m2-surface-container p-6 rounded-lg shadow-sm">
        <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-m2-on-surface">Đăng ký Workshop</h1>
            <p className="text-sm text-m2-on-surface-variant mt-1">AI4SDLC Workshop · 27/09/2026 · 20:00–22:00</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {total > 0 && <span className="text-sm text-m2-on-surface-variant">Tổng: <strong>{total}</strong> đăng ký</span>}
            <button
              type="button"
              onClick={() => setShowSendModal(true)}
              disabled={isSending}
              title={isSending ? 'Đang gửi, vui lòng chờ...' : 'Gửi link Google Meet cho tất cả người đăng ký'}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isSending ? (
                <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" /> Đang gửi...</>
              ) : (
                <><svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M15 8v8H5V8h10m1-2H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4V7c0-.55-.45-1-1-1z" /></svg>Gửi Meet Link</>
              )}
            </button>
            <button type="button" onClick={handleExportCsv} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border border-m2-primary text-m2-primary hover:bg-m2-primary/10 transition-colors">
              📥 Xuất CSV
            </button>
          </div>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <input
            type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Tìm theo tên, email hoặc số điện thoại..."
            className="flex-1 px-3 py-2 rounded-lg border border-m2-outline-variant bg-m2-input text-m2-on-surface text-sm focus:outline-none focus:ring-1 focus:ring-m2-primary"
          />
          <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium text-m2-on-primary bg-m2-primary hover:bg-eh-forest-shadow transition-colors">Tìm kiếm</button>
          {search && (
            <button type="button" onClick={() => { setSearchInput(''); setSearch(''); setPage(1) }}
              className="px-3 py-2 rounded-lg text-sm text-m2-on-surface-variant border border-m2-outline-variant hover:bg-m2-surface-low transition-colors">
              ✕ Xóa
            </button>
          )}
        </form>

        {loading ? (
          <div className="py-16 text-center text-m2-on-surface-variant text-sm">Đang tải...</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center"><p className="text-m2-on-surface-variant text-sm">{search ? 'Không tìm thấy kết quả phù hợp.' : 'Chưa có đăng ký nào.'}</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-m2-outline-variant">
              <thead className="bg-m2-surface-low">
                <tr>
                  {['#', 'Họ và tên', 'Email', 'Số điện thoại', 'Trạng thái Email', 'Ngày đăng ký', 'Hành động'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-m2-on-surface-variant uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-m2-surface-container divide-y divide-m2-outline-variant">
                {items.map((item, i) => (
                  <tr key={item.id} className="hover:bg-m2-surface-low/50 transition-colors">
                    <td className="px-4 py-3 text-sm text-m2-on-surface-variant whitespace-nowrap">{(page - 1) * 20 + i + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-m2-on-surface whitespace-nowrap">{item.full_name}</td>
                    <td className="px-4 py-3 text-sm text-m2-on-surface-variant whitespace-nowrap">
                      <a href={`mailto:${item.email}`} className="hover:underline text-m2-primary">{item.email}</a>
                    </td>
                    <td className="px-4 py-3 text-sm text-m2-on-surface-variant whitespace-nowrap">{item.phone}</td>
                    <td className="px-4 py-3 text-sm text-m2-on-surface-variant">
                      {item.email_status ? statusBadge(item.email_status) : <span className="text-m2-on-surface-variant/40">Chưa gửi</span>}
                      {item.email_sent_at && <div className="text-xs mt-1 text-m2-on-surface-variant/60">{formatDbDateTimeVi(item.email_sent_at)}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-m2-on-surface-variant whitespace-nowrap">{formatDbDateTimeVi(item.registered_at)}</td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <button
                        onClick={() => handleResendSingle(item.id)}
                        disabled={isSending}
                        className="text-blue-600 hover:text-blue-800 text-xs font-semibold px-2 py-1 rounded bg-blue-50 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                      >
                        Gửi lại
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-m2-outline-variant">
            <span className="text-sm text-m2-on-surface-variant">Trang {page} / {totalPages}</span>
            <div className="flex gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1.5 text-sm rounded-md border border-m2-outline-variant text-m2-on-surface hover:bg-m2-surface-low disabled:opacity-40 disabled:cursor-not-allowed transition-colors">← Trước</button>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 text-sm rounded-md border border-m2-outline-variant text-m2-on-surface hover:bg-m2-surface-low disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Tiếp →</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Email Log Section ─── */}
      <div className="bg-m2-surface-container rounded-lg shadow-sm overflow-hidden">
        <button type="button" onClick={() => setShowLogs((v) => !v)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-m2-surface-low transition-colors">
          <div className="flex items-center gap-3">
            <span className="text-lg">📋</span>
            <div>
              <h2 className="text-base font-semibold text-m2-on-surface flex items-center gap-2">
                Log Gửi Email Meet Link
                {isSending && <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse inline-block" />Live</span>}
              </h2>
              {logStats && (
                <p className="text-xs text-m2-on-surface-variant mt-0.5">
                  Tổng: {logStats.total} ·&nbsp;
                  <span className="text-green-600">✅ {logStats.success_count}</span> ·&nbsp;
                  <span className="text-red-600">❌ {logStats.failed_count}</span> ·&nbsp;
                  <span className="text-yellow-600">⏳ {logStats.pending_count}</span>
                </p>
              )}
            </div>
          </div>
          <span className="text-m2-on-surface-variant text-sm">{showLogs ? '▲ Thu gọn' : '▼ Xem log'}</span>
        </button>

        {showLogs && (
          <div className="border-t border-m2-outline-variant px-6 py-4">
            {/* Stats cards */}
            {logStats && Number(logStats.total) > 0 && (
              <div className="grid grid-cols-4 gap-3 mb-5">
                {[
                  { label: 'Tổng', value: logStats.total, color: 'bg-m2-surface-high text-m2-on-surface' },
                  { label: 'Thành công', value: logStats.success_count, color: 'bg-green-100 text-green-800' },
                  { label: 'Thất bại', value: logStats.failed_count, color: 'bg-red-100 text-red-800' },
                  { label: 'Đang gửi', value: logStats.pending_count, color: 'bg-yellow-100 text-yellow-800' },
                ].map((s) => (
                  <div key={s.label} className={`rounded-lg px-4 py-3 text-center ${s.color}`}>
                    <div className="text-2xl font-bold">{s.value}</div>
                    <div className="text-xs mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <button type="button" onClick={() => void loadLogs()} disabled={logsLoading || isSending}
                className="px-3 py-1.5 text-sm rounded-md border border-m2-outline-variant text-m2-on-surface hover:bg-m2-surface-low transition-colors disabled:opacity-50">
                {isSending ? '🔄 Auto-refresh đang bật...' : logsLoading ? '⏳ Đang tải...' : '🔄 Làm mới'}
              </button>
              {failedLogs.length > 0 && (
                <>
                  <button type="button" onClick={handleRetry} disabled={retrying || isSending}
                    className="px-4 py-1.5 text-sm rounded-md font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                    {retrying ? <><span className="animate-spin inline-block">⏳</span> Đang retry...</> : <>🔁 Retry {selectedFailedIds.length > 0 ? `${selectedFailedIds.length} đã chọn` : `tất cả (${failedLogs.length})`}</>}
                  </button>
                  {selectedFailedIds.length > 0 && (
                    <button type="button" onClick={() => setSelectedFailedIds([])} className="text-xs text-m2-on-surface-variant hover:underline">Bỏ chọn</button>
                  )}
                </>
              )}
            </div>

            {/* Log table */}
            {logs.length === 0 ? (
              <div className="py-10 text-center text-m2-on-surface-variant text-sm">Chưa có log nào. Hãy gửi email Meet Link trước.</div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-m2-outline-variant">
                <table className="min-w-full divide-y divide-m2-outline-variant text-sm">
                  <thead className="bg-m2-surface-low">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-medium text-m2-on-surface-variant uppercase">
                        {failedLogs.length > 0 && (
                          <input type="checkbox" className="accent-orange-500"
                            checked={selectedFailedIds.length === failedLogs.length && failedLogs.length > 0}
                            onChange={(e) => setSelectedFailedIds(e.target.checked ? failedLogs.map((l) => l.id) : [])} />
                        )}
                      </th>
                      {['#', 'Họ tên', 'Email', 'Trạng thái', 'Thời gian gửi', 'Tạo lúc'].map((h) => (
                        <th key={h} className="px-3 py-2.5 text-left text-xs font-medium text-m2-on-surface-variant uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-m2-outline-variant">
                    {logs.map((log, i) => (
                      <tr key={log.id} className={`transition-colors ${log.status === 'failed' ? 'bg-red-50/40 hover:bg-red-50' : log.status === 'pending' ? 'bg-yellow-50/40' : 'hover:bg-m2-surface-low/30'}`}>
                        <td className="px-3 py-2.5">
                          {log.status === 'failed' && (
                            <input type="checkbox" className="accent-orange-500"
                              checked={selectedFailedIds.includes(log.id)}
                              onChange={() => toggleFailedSelect(log.id)} />
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-m2-on-surface-variant">{i + 1}</td>
                        <td className="px-3 py-2.5 font-medium text-m2-on-surface whitespace-nowrap">{log.full_name}</td>
                        <td className="px-3 py-2.5 text-m2-on-surface-variant whitespace-nowrap">{log.email}</td>
                        <td className="px-3 py-2.5">{statusBadge(log.status)}</td>
                        <td className="px-3 py-2.5 text-m2-on-surface-variant whitespace-nowrap text-xs">{log.sent_at ? formatDbDateTimeVi(log.sent_at) : '—'}</td>
                        <td className="px-3 py-2.5 text-m2-on-surface-variant whitespace-nowrap text-xs">{formatDbDateTimeVi(log.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminWorkshopRegistrationsPage
